import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getTelegramStatus } from '../../services/telegramService';
import * as React from 'react';


interface TelegramNotificationBannerProps {
  variant?: 'compact' | 'full';
  showCloseButton?: boolean;
  className?: string;
}

const TelegramNotificationBanner: React.FC<TelegramNotificationBannerProps> = ({ 
  variant = 'full', 
  showCloseButton = true,
  className = '' 
}) => {
  const { user } = useAuth();
  const [telegramLinked, setTelegramLinked] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user) {
      checkTelegramStatus();
      // Vérifier si l'utilisateur a déjà fermé la bannière (stockage local)
      const dismissedKey = `telegram-banner-dismissed-${user.id}`;
      const wasDismissed = localStorage.getItem(dismissedKey) === 'true';
      setDismissed(wasDismissed);
    }
  }, [user]);

  const checkTelegramStatus = async () => {
    try {
      const status = await getTelegramStatus();
      setTelegramLinked(status.linked);
    } catch (error) {
      console.error('Erreur lors de la vérification du statut Telegram:', error);
      setTelegramLinked(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (user) {
      const dismissedKey = `telegram-banner-dismissed-${user.id}`;
      localStorage.setItem(dismissedKey, 'true');
    }
  };

  // Ne pas afficher si :
  // - L'utilisateur n'est pas connecté
  // - Le Telegram est déjà lié
  // - La bannière a été fermée
  // - Encore en cours de chargement
  if (!user || telegramLinked || dismissed || loading) {
    return null;
  }

  if (variant === 'compact') {
    return (
      <div className={`bg-blue-50 border-l-4 border-blue-400 p-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span className="text-sm text-blue-800">
              📱 Activez les notifications Telegram pour ne rien manquer !
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Link 
              to="/profile"
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition"
            >
              Activer
            </Link>
            {showCloseButton && (
              <button 
                onClick={handleDismiss}
                className="text-blue-600 hover:text-blue-800 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-3">
            <div className="bg-white bg-opacity-20 rounded-full p-2 mr-3">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.169 1.858-.896 6.728-.896 6.728-.302 1.507-1.124 1.771-1.621 1.771-.823 0-1.011-1.002-1.011-1.002l-2.262-9.485s-.175-.97.556-.97c.73 0 1.066.645 1.066.645l1.781 7.058s.096.466.553.466.457-.463.457-.463l1.376-6.748z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold">🚀 Restez connecté avec Telegram !</h3>
              <p className="text-blue-100 text-sm">Activez les notifications pour une expérience optimale</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Notifications instantanées</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Mises à jour importantes</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Offres exclusives</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/profile"
              className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-lg font-medium transition inline-flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Activer maintenant
            </Link>
            <button 
              onClick={handleDismiss}
              className="text-blue-100 hover:text-white border border-blue-300 hover:border-white px-4 py-2 rounded-lg transition"
            >
              Plus tard
            </button>
          </div>
        </div>
        
        {showCloseButton && (
          <button 
            onClick={handleDismiss}
            className="text-blue-200 hover:text-white transition ml-4"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default TelegramNotificationBanner;